"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, Compass, Cpu, Landmark, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
interface BranchItem {
  id: string;
  name: string;
  role: string;
  code: string;
  iconName: string;
}
const branches: BranchItem[] = [{
  id: "branch-1",
  name: "Phân hội Khoa học Địa lý Phong thủy Việt Nam",
  role: "Nghiên cứu ứng dụng khoa học địa lý cảnh quan và không gian phong thủy truyền thống",
  code: "Đơn vị chuyên ngành",
  iconName: "Compass"
}, {
  id: "branch-2",
  name: "Trung tâm Khoa học công nghệ Địa lý Môi trường Việt Nam (CEGTES)",
  role: "Ứng dụng khoa học công nghệ địa lý môi trường, quan trắc và giải pháp phát triển bền vững",
  code: "CEGTES",
  iconName: "Cpu"
}, {
  id: "branch-3",
  name: "Viện Địa lý môi trường (IEG)",
  role: "Đào tạo, nghiên cứu chuyên sâu các quy luật địa lý sinh thái và chính sách bảo vệ tài nguyên",
  code: "IEG",
  iconName: "Landmark"
}, {
  id: "branch-4",
  name: "Trung tâm nghiên cứu giá trị văn hóa - tôn giáo vì môi trường phát triển bền vững",
  role: "Bảo tồn giá trị văn hóa, tri thức bản địa và gắn kết tín ngưỡng vì bảo vệ sinh thái",
  code: "Trung tâm nghiên cứu",
  iconName: "Sparkles"
}];
export default function Hierarchy() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        duration: 0.38,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="sơ-đồ-tổ-chức-tổng-quan" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header with Archival Dual-Bar Framing */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-sm border border-border bg-accent text-accent-foreground text-xs font-semibold uppercase tracking-wider">
            Cơ cấu tổ chức bộ máy
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-secondary">
            Sơ đồ tổ chức tổng quan
          </h1>
          <div className="mt-3 flex items-center gap-2">
            <div className="h-1 w-12 bg-secondary rounded-xs" />
            <div className="h-px flex-1 bg-border" />
          </div>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Hệ thống cơ cấu tổ chức chính thức của Hội Địa lý môi trường Việt Nam theo Quyết định số 483/QĐ-BNV và Điều lệ Hội phê duyệt theo Quyết định số 1157/QĐ-BNV.
          </p>
        </div>

        {/* Tree Container */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="flex flex-col items-center">
          {/* Top Level: Central Leadership */}
          <motion.div variants={itemVariants} className="w-full max-w-xl">
            <Card className="relative overflow-hidden border border-border bg-card shadow-sm rounded-sm text-center">
              <div className="h-1.5 w-full bg-primary" />
              <CardHeader className="pt-6 pb-2 px-6">
                <div className="mx-auto size-12 rounded-sm bg-accent text-primary flex items-center justify-center mb-3">
                  <Building2 className="size-6" />
                </div>
                <div className="text-xs uppercase tracking-widest text-primary font-semibold">
                  Cơ quan lãnh đạo trung ương
                </div>
                <CardTitle className="text-xl sm:text-2xl font-bold text-foreground mt-1">
                  Hội Địa lý môi trường Việt Nam
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-6 px-6 text-sm text-muted-foreground">
                Tổ chức xã hội - nghề nghiệp quy tụ các chuyên gia, nhà khoa học và tổ chức hoạt động trong lĩnh vực địa lý môi trường trên phạm vi cả nước.
              </CardContent>
            </Card>
          </motion.div>

          {/* Central Vertical Connector */}
          <div className="w-px h-10 md:h-12 bg-border relative">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-2 rounded-full bg-primary" />
          </div>

          {/* Horizontal Distribution Line on Desktop */}
          <div className="hidden lg:block w-full max-w-5xl relative">
            <div className="h-px w-full bg-border" />
            <div className="absolute -top-1 left-0 size-2 rounded-full bg-border" />
            <div className="absolute -top-1 right-0 size-2 rounded-full bg-border" />
            <div className="grid grid-cols-4 w-full">
              {[0, 1, 2, 3].map(idx => <div key={idx} className="flex justify-center">
                  <div className="w-px h-8 bg-border" />
                </div>)}
            </div>
          </div>

          {/* Subordinate Branches Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full mt-2 lg:mt-0">
            {branches.map((branch, i) => <motion.div key={branch.id} data-index={i} variants={itemVariants} className="flex flex-col h-full">
                {/* Mobile/Tablet vertical bridge indicator */}
                <div className="block lg:hidden w-px h-4 bg-border mx-auto" />

                <Card className="relative flex-1 flex flex-col justify-between border border-border bg-card rounded-sm shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-200 hover:border-primary/50">
                  <div className="h-1 w-full bg-secondary" />
                  <CardHeader className="pt-5 pb-3 px-5">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="size-9 rounded-sm bg-accent text-primary flex items-center justify-center">
                        {branch.iconName === "Compass" && <Compass className="size-5" />}
                        {branch.iconName === "Cpu" && <Cpu className="size-5" />}
                        {branch.iconName === "Landmark" && <Landmark className="size-5" />}
                        {branch.iconName === "Sparkles" && <Sparkles className="size-5" />}
                      </div>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-sm bg-muted text-muted-foreground border border-border">
                        {branch.code}
                      </span>
                    </div>
                    <CardTitle className="text-base font-semibold text-foreground leading-snug">
                      {branch.name}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="px-5 pb-5 text-sm text-muted-foreground flex-1 flex flex-col justify-between">
                    <p className="leading-relaxed">{branch.role}</p>
                    <div className="pt-4 mt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground font-medium">
                      <span>Trực thuộc Hội</span>
                      <span className="text-primary font-semibold">Quy chế VAEG</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>

          {/* Bottom Action / Inquiry Guidance */}
          <motion.div variants={itemVariants} className="mt-12 p-6 w-full max-w-3xl rounded-sm border border-border bg-muted/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
            <div>
              <h2 className="text-sm font-semibold text-foreground">
                Tra cứu tài liệu quy chế tổ chức
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Xem điều lệ, chức năng nhiệm vụ và văn bản pháp lý các đơn vị thành viên.
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Button asChild variant="outline" size="sm" className="rounded-sm">
                <Link to="/ContactPage">Liên hệ văn phòng</Link>
              </Button>
              <Button asChild variant="default" size="sm" className="rounded-sm">
                <Link to="/AboutPage">Về chúng tôi</Link>
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}